import { errorToast } from 'lib/toast'
import { waitForGlobal } from 'lib/wait-for-global'

// Kept short deliberately. The wait is memoized so it's paid once per session,
// but it's paid *simultaneously* by every in-flight request on the first page —
// so a blocked CDN (ad blocker, corporate proxy, regional block) would otherwise
// stall the whole app's first render behind it.
const MAX_WAIT_TIME = 1500
const POLL_INTERVAL = 100

//
// Module-level memoization so a slow or failed SDK load is paid for once, not on
// every request. Without it, the request interceptor that awaits getWafToken()
// would stall every API call for the full timeout and fire a toast each time
// whenever the SDK can't load.
//
let sdkWaitPromise: Promise<boolean> | null = null
let sdkUnavailable = false
let timeoutToastShown = false

//
// Both halves matter, and they must agree with routes/ssr/Head.js: `enabled` and
// `sdkUrl` are *separate* env overrides, so an environment that sets `enabled`
// without inheriting `sdkUrl` from default.json would otherwise mean no script
// tag is ever emitted while every session still pays the poll and shows the
// failure toast.
//
export const isWafEnabled = (): boolean =>
  typeof window !== 'undefined' && Boolean(CONFIG.awsWaf?.enabled && CONFIG.awsWaf?.sdkUrl)

// Poll for the SDK exactly once; concurrent and later callers share this promise.
const waitForSdk = (): Promise<boolean> => {
  if (!sdkWaitPromise) {
    sdkWaitPromise = waitForGlobal(() => typeof window.AwsWafIntegration !== 'undefined', {
      intervalMs: POLL_INTERVAL,
      timeoutMs: MAX_WAIT_TIME
    }).then(ready => {
      if (!ready) {
        sdkUnavailable = true
      }
      return ready
    })
  }

  return sdkWaitPromise
}

//
// Acquire an AWS WAF token via the Application Integration SDK. The SDK caches
// the token in the `aws-waf-token` cookie (scoped by window.awsWafCookieDomainList,
// set in routes/ssr/Head.js) and refreshes it as needed, so callers can invoke
// this freely — across SPA navigations the memoized state answers immediately,
// and across full page loads the cookie means getToken() needs no network hop.
//
// Returns null when WAF is disabled, when running server-side, or when the SDK
// never loads. Callers treat null as "send it anyway": the WAF starts out in
// COUNT mode, so a missing token must not stop the request — the edge decides.
// See client/lib/api/index.js.
//
export const getWafToken = async (): Promise<string | null> => {
  if (!isWafEnabled()) {
    return null
  }

  // The SDK already timed out once — fail fast instead of re-polling per request.
  if (sdkUnavailable) {
    return null
  }

  const ready = await waitForSdk()
  if (!ready) {
    // Once per session, not once per request: the interceptor calls this on
    // every request, and the memoized state above is what keeps it to one.
    if (!timeoutToastShown) {
      timeoutToastShown = true
      errorToast({
        message:
          'There was an issue verifying your request. Please make sure your internet connection is stable and try again.',
        durationMs: 5500
      })
    }
    return null
  }

  try {
    return await window.AwsWafIntegration!.getToken()
  } catch (e) {
    console.error(e)
    return null
  }
}

// Test-only: clear the memoized SDK-load state between cases.
export const __resetWafSdkState = (): void => {
  sdkWaitPromise = null
  sdkUnavailable = false
  timeoutToastShown = false
}

//
// Render the interactive AWS WAF CAPTCHA puzzle into the given container, used
// when WAF answers a request with a CAPTCHA challenge (HTTP 405). Resolves with
// the solved token; the SDK also persists it to the `aws-waf-token` cookie, so
// the retried request and everything after it are covered.
//
export const solveCaptcha = (containerEl: HTMLElement): Promise<string> =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof window.AwsWafCaptcha === 'undefined') {
      reject(new Error('AWS WAF CAPTCHA SDK is not available'))
      return
    }

    try {
      window.AwsWafCaptcha.renderCaptcha(containerEl, {
        apiKey: CONFIG.awsWaf.captchaApiKey,
        onSuccess: token => resolve(token),
        onError: err => reject(err instanceof Error ? err : new Error(String(err)))
      })
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)))
    }
  })
